/**
 * The sound board: one sprite, one `AudioContext`, and a hard rule that neither exists until the
 * visitor has asked for sound and then touched the page.
 *
 * **Muted is the product decision, not a default someone forgot to change.** A page that makes
 * noise at a stranger who opened a link from a chat is a page they close, and every browser now
 * blocks it anyway. So nothing here constructs an `AudioContext` on import, on mount, or on the
 * store's first `hello`: the constructor runs inside a real user gesture, once, from
 * `SoundToggle`. Until then `ctx` is `null` and every hook below is a no-op that costs a boolean.
 * That is also what makes the sprite request honest — `/sounds/sprite.webm` is not fetched on page
 * load at all, so a visitor who never turns sound on never pays 98 kB for it.
 *
 * **Why raw WebAudio.** `docs/architecture/frontend.md` picks it over Howler: the whole feature is
 * one decoded buffer, a handful of `BufferSource` slices and two looping beds, and a library that
 * costs 10 kB to spare us thirty lines is a bad trade on a page with a 250 kB JS budget. Slicing a
 * sprite also gets the latency right for free — a `BufferSource` scheduled on an already-decoded
 * buffer starts on the next audio quantum, so the thud lands with the fist rather than after it.
 *
 * **Reduced motion deliberately does not gate sound.** `prefers-reduced-motion` is about
 * vestibular safety, and a visitor who has turned animation off and sound on has asked for exactly
 * that. The toggle is the only control over audio.
 *
 * Every method below is safe to call at any time: before unlock, while muted, mid-decode, after
 * `dispose`. The scene calls them from a frame loop and must never have to ask whether sound is on.
 */
import type { ClickTarget } from '@/net/transport'
import { createLimiter, type Limiter } from './limiter'
import { parseSpriteMap, SPRITE_NAMES, type Bed, type SpriteMap, type SpriteName } from './sprite'

/** Everything is quieter than the mix that produced it; the sprite peaks near full scale. */
const MASTER_GAIN = 0.6

type Kind = 'thud' | 'groan' | 'gate' | 'fanfare' | 'voice' | 'ambient'

/**
 * Per-kind gain (F21 §1).
 *
 * These are a mix, not a volume control: thuds are the most frequent sound on the page and sit
 * lowest so that the groans — which carry the joke — stay on top of them, and the beds sit far
 * enough down that a ten-minute siege is background rather than a drone in the room.
 */
const KIND_GAIN: Record<Kind, number> = {
  thud: 0.5,
  groan: 0.8,
  gate: 1,
  fanfare: 0.9,
  voice: 0.7,
  ambient: 0.25,
}

const KIND_OF: Record<SpriteName, Kind> = {
  groan_low: 'groan',
  groan_high: 'groan',
  thud_soft: 'thud',
  thud_wall: 'thud',
  gate_crash: 'gate',
  rebuild_hammer: 'thud',
  cheer: 'voice',
  reset_fanfare: 'fanfare',
  ambient_day: 'ambient',
  ambient_siege: 'ambient',
}

const CROSSFADE_SECONDS = 1.5

/**
 * Muting is not a crossfade. A visitor who just hit the toggle wants silence now, and 1.5 s of
 * bed after the click reads as a control that did not work — but an abrupt stop clicks, so the
 * bed still ramps, just fast enough to be heard as "off".
 */
const MUTE_FADE_SECONDS = 0.2

/** Points in the crossfade curve. Enough that the steps are far below the ear's resolution. */
const FADE_POINTS = 64

const REBUILD_HITS = 3
const REBUILD_SPACING_SECONDS = 0.5

/** The cheer gets the courtyard to itself before peace comes back under it. */
const ALL_CLEAR_TO_DAY_MS = 2_000

const PRESS_PITCH_SPREAD = 0.05
const IMPACT_PITCH_SPREAD = 0.1

export const SOUND_STORAGE_KEY = 'mha.sound'

const SPRITE_JSON_URL = '/sounds/sprite.json'
const SPRITE_WEBM_URL = '/sounds/sprite.webm'
const SPRITE_M4A_URL = '/sounds/sprite.m4a'

export interface PlayOptions {
  /** `playbackRate`, so pitch and length move together — a groan slowed is a groan, not a chipmunk. */
  pitch?: number
  gain?: number
  /** Seconds from now. Used to space the rebuild hammer without a chain of timers. */
  delay?: number
}

export interface SoundBoard {
  /** `null` until the first gesture after sound is turned on. Read by tests and by `window.__mha`. */
  readonly ctx: AudioContext | null
  readonly enabled: boolean
  /** The sprite is decoded and playable. */
  readonly ready: boolean
  /** Apply the stored preference, returning it. Does not create a context. */
  restore: () => boolean
  setEnabled: (on: boolean) => void
  /** Must be called from inside a user gesture. Cheap and idempotent after the first time. */
  unlock: () => void
  play: (name: SpriteName, options?: PlayOptions) => boolean
  setAmbient: (bed: Bed) => void
  press: (target: ClickTarget) => void
  /** A fist — or, for the one zombie in ten carrying a laptop, `soft` — landing on the wall. */
  impact: (seed: number, soft?: boolean) => void
  groan: (square: string, sample: string | undefined, pitch: number) => void
  /** The gate, or a v2 wall, coming down. `pitch` rises per fall on `/v2` (F28); the breach plays it at 1. */
  gateCrash: (square: string, pitch?: number) => void
  rebuild: () => void
  /** A square is under siege. State, not punctuation: safe to call on every reconnect. */
  siegeStart: (square: string) => void
  /** A square is at peace again. Hands the bed back to birdsong once the cheer has had its beat. */
  siegeEnd: (square: string) => void
  /** The all-clear crowd. Punctuation: only ever called for a siege the page actually watched end. */
  cheer: () => void
  codexReset: () => void
  dispose: () => void
}

export interface SoundDeps {
  /** `null` when the browser has no WebAudio at all, which is not an error worth surfacing. */
  createContext: () => AudioContext | null
  /** The offset table and the encoded sprite in whichever codec this browser can decode. */
  load: () => Promise<{ map: SpriteMap; encoded: ArrayBuffer }>
  /** Monotonic milliseconds for the rate limiter. */
  now: () => number
  /** Runs the decode off the critical path. */
  idle: (run: () => void) => void
  storage: () => Storage | null
}

function safeStorage(): Storage | null {
  try {
    // The getter itself throws when site data is blocked, so this is not paranoia about `null`.
    return window.localStorage
  } catch {
    return null
  }
}

function defaultLoad(): Promise<{ map: SpriteMap; encoded: ArrayBuffer }> {
  // Opus in WebM everywhere it decodes; the AAC copy exists for Safari alone. `canPlayType` is
  // asked rather than the user agent because that is the question we actually have.
  const probe = document.createElement('audio')
  const webm = probe.canPlayType('audio/webm; codecs="opus"') !== ''
  const audioUrl = webm ? SPRITE_WEBM_URL : SPRITE_M4A_URL
  return Promise.all([
    fetch(SPRITE_JSON_URL).then((response) => response.json() as Promise<unknown>),
    fetch(audioUrl).then((response) => response.arrayBuffer()),
  ]).then(([json, encoded]) => ({ map: parseSpriteMap(json), encoded }))
}

function defaultDeps(): SoundDeps {
  return {
    createContext: () => {
      const Ctor = window.AudioContext
      return typeof Ctor === 'function' ? new Ctor() : null
    },
    load: defaultLoad,
    now: () => performance.now(),
    idle: (run) => {
      // Called through `window` rather than through a saved reference: `requestIdleCallback` is a
      // method of the window and throws "Illegal invocation" when it is called detached from it.
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(() => {
          run()
        }, { timeout: 2_000 })
      } else {
        window.setTimeout(run, 0)
      }
    },
    storage: safeStorage,
  }
}

/**
 * The equal-power half of a crossfade, from `from` to `to`.
 *
 * Equal power rather than linear because the two beds are uncorrelated noise: crossfaded linearly
 * their sum dips about 3 dB in the middle, which is heard as the room briefly emptying. Starting
 * the curve at the parameter's *current* value is what makes an interrupted fade — a breach
 * arriving mid-crossfade — continue rather than jump, and a jump in a gain parameter is the click
 * this shape exists to avoid.
 */
function fadeCurve(from: number, to: number): Float32Array {
  const curve = new Float32Array(FADE_POINTS)
  for (let i = 0; i < FADE_POINTS; i++) {
    const x = i / (FADE_POINTS - 1)
    // Rising rides sin, falling rides cos, so the two beds' powers sum to one throughout:
    // sin²+cos² = 1. Written as one interpolation so an interrupted fade keeps its start value.
    const shape = to >= from ? Math.sin((Math.PI / 2) * x) : 1 - Math.cos((Math.PI / 2) * x)
    curve[i] = from + (to - from) * shape
  }
  curve[FADE_POINTS - 1] = to
  return curve
}

interface BedVoice {
  name: SpriteName
  source: AudioBufferSourceNode
  gain: GainNode
  /** Context time the current fade started, and the value it started from — see `fadeCurve`. */
  fadeStart: number
  fadeFrom: number
  fadeTo: number
}

export function createSoundEngine(overrides: Partial<SoundDeps> = {}): SoundBoard {
  const deps: SoundDeps = { ...defaultDeps(), ...overrides }
  const limiter: Limiter = createLimiter()

  let ctx: AudioContext | null = null
  let master: GainNode | null = null
  let kindGains: Record<Kind, GainNode> | null = null
  let buffer: AudioBuffer | null = null
  let map: SpriteMap | null = null
  let enabled = false
  let loading = false
  let disposed = false
  let bed: Bed = 'day'
  let voice: BedVoice | null = null
  let dayTimer: ReturnType<typeof setTimeout> | null = null
  /**
   * Which squares are under siege, so the bed follows the *page* rather than the last event. With
   * three squares a single `allClear` must not put the site back to birdsong while another
   * provider is still being hammered.
   */
  const besieged = new Set<string>()

  function ready(): boolean {
    return buffer !== null && map !== null && kindGains !== null && ctx !== null
  }

  function persist(on: boolean): void {
    try {
      deps.storage()?.setItem(SOUND_STORAGE_KEY, on ? 'on' : 'off')
    } catch {
      // Private mode and blocked site data both throw here. The preference is a convenience;
      // losing it costs the visitor one click next visit, and there is nothing to report.
    }
  }

  function region(name: SpriteName): readonly [number, number] | null {
    return map?.[name] ?? null
  }

  function schedule(name: SpriteName, options: PlayOptions = {}): boolean {
    if (!enabled || disposed || !ready()) return false
    const slice = region(name)
    if (!slice || !ctx || !kindGains) return false
    const [start, duration] = slice
    const source = ctx.createBufferSource()
    source.buffer = buffer
    const pitch = options.pitch ?? 1
    if (pitch !== 1) source.playbackRate.value = pitch

    const kindGain = kindGains[KIND_OF[name]]
    const extra = options.gain ?? 1
    if (extra === 1) {
      source.connect(kindGain)
    } else {
      const trim = ctx.createGain()
      trim.gain.value = extra
      source.connect(trim)
      trim.connect(kindGain)
    }
    // `duration` is measured in the buffer, so a pitched-down slice plays longer without arithmetic
    // here — and, crucially, never runs past its region into the next sound's attack.
    source.start(ctx.currentTime + (options.delay ?? 0), start, duration)
    return true
  }

  /** Fade `voice` to `to`, continuing smoothly from wherever an interrupted fade had got to. */
  function fadeTo(target: BedVoice, to: number, seconds = CROSSFADE_SECONDS): void {
    if (!ctx) return
    const now = ctx.currentTime
    const elapsed = now - target.fadeStart
    const progress = elapsed >= CROSSFADE_SECONDS ? 1 : Math.max(0, elapsed / CROSSFADE_SECONDS)
    const current = target.fadeFrom + (target.fadeTo - target.fadeFrom) * progress
    target.fadeStart = now
    target.fadeFrom = current
    target.fadeTo = to
    target.gain.gain.cancelScheduledValues(now)
    target.gain.gain.setValueCurveAtTime(fadeCurve(current, to), now, seconds)
  }

  function stopBed(seconds = CROSSFADE_SECONDS): void {
    if (!voice || !ctx) return
    const going = voice
    voice = null
    fadeTo(going, 0, seconds)
    // Stopped a beat after the fade lands: stopping *at* the end of a curve occasionally clips its
    // last quantum, and a bed that ends on a step is the one click a crossfade cannot hide.
    going.source.stop(ctx.currentTime + seconds + 0.05)
  }

  function applyBed(): void {
    if (!enabled || disposed || !ready() || !ctx || !kindGains) return
    const wanted: SpriteName | null =
      bed === 'none' ? null : bed === 'day' ? 'ambient_day' : 'ambient_siege'
    if (voice && voice.name === wanted) return
    stopBed()
    if (wanted === null) return
    const slice = region(wanted)
    if (!slice) return
    const [start, duration] = slice
    const source = ctx.createBufferSource()
    source.buffer = buffer
    source.loop = true
    source.loopStart = start
    source.loopEnd = start + duration
    const gain = ctx.createGain()
    gain.gain.value = 0
    source.connect(gain)
    gain.connect(kindGains.ambient)
    source.start(ctx.currentTime, start)
    voice = {
      name: wanted,
      source,
      gain,
      fadeStart: ctx.currentTime,
      fadeFrom: 0,
      fadeTo: 0,
    }
    fadeTo(voice, 1)
  }

  function buildGraph(created: AudioContext): void {
    master = created.createGain()
    master.gain.value = MASTER_GAIN
    master.connect(created.destination)
    const gains = {} as Record<Kind, GainNode>
    for (const name of SPRITE_NAMES) {
      const kind = KIND_OF[name]
      if (gains[kind]) continue
      const node = created.createGain()
      node.gain.value = KIND_GAIN[kind]
      node.connect(master)
      gains[kind] = node
    }
    kindGains = gains
  }

  function load(): void {
    if (loading || buffer !== null || !ctx) return
    loading = true
    const created = ctx
    deps
      .load()
      .then(async (result) => {
        const decoded = await created.decodeAudioData(result.encoded)
        if (disposed) return
        buffer = decoded
        map = result.map
        applyBed()
      })
      .catch((error: unknown) => {
        // A sprite that will not decode is a page that stays silent, which is survivable; the
        // alternative — throwing out of an idle callback — is an error report with no stack that
        // says anything about audio.
        console.warn('[sound] sprite unavailable, staying silent', error)
      })
      .finally(() => {
        loading = false
      })
  }

  function onVisibility(): void {
    // Browsers suspend a context whose tab went to the background, and a suspended context that is
    // never resumed is a page that is silently muted for the rest of the session.
    if (document.visibilityState === 'visible' && ctx && ctx.state === 'suspended') {
      void ctx.resume()
    }
  }

  function unlock(): void {
    if (disposed) return
    if (ctx) {
      if (ctx.state === 'suspended') void ctx.resume()
      return
    }
    const created = deps.createContext()
    if (!created) return
    ctx = created
    buildGraph(created)
    document.addEventListener('visibilitychange', onVisibility)
    // The decode is ~25 s of audio; doing it in the gesture would block the frame the visitor is
    // looking at, and nothing is waiting on it — the first sound after unlock is allowed to be
    // the second sound the page wanted to make.
    deps.idle(load)
  }

  function ambientForState(): Bed {
    return besieged.size > 0 ? 'siege' : 'day'
  }

  function clearDayTimer(): void {
    if (dayTimer !== null) {
      clearTimeout(dayTimer)
      dayTimer = null
    }
  }

  function pitched(spread: number, unit: number): number {
    return 1 - spread + spread * 2 * unit
  }

  const board: SoundBoard = {
    get ctx() {
      return ctx
    },
    get enabled() {
      return enabled
    },
    get ready() {
      return ready()
    },

    restore(): boolean {
      let stored = false
      try {
        stored = deps.storage()?.getItem(SOUND_STORAGE_KEY) === 'on'
      } catch {
        // Blocked site data throws on read as well as on write. Muted is the safe reading.
      }
      enabled = stored
      return stored
    },

    setEnabled(on: boolean): void {
      if (enabled === on) return
      enabled = on
      persist(on)
      if (!on) {
        clearDayTimer()
        stopBed(MUTE_FADE_SECONDS)
        return
      }
      applyBed()
    },

    unlock,

    play(name, options = {}) {
      return schedule(name, options)
    },

    setAmbient(next: Bed): void {
      bed = next
      applyBed()
    },

    press(_target: ClickTarget): void {
      // The press tick shares the wall-thud budget on purpose: a spam-clicker during a siege
      // should hear one crowd, not their own click track fighting twenty zombies.
      if (!limiter.allow('thud', deps.now())) return
      schedule('thud_soft', { pitch: pitched(PRESS_PITCH_SPREAD, Math.random()) })
    },

    impact(seed: number, soft = false): void {
      if (!limiter.allow('thud', deps.now())) return
      // Pitch from the zombie's own seed, so a given zombie always hits the wall in its own voice
      // and twenty of them read as twenty fists rather than one sample repeated.
      //
      // `soft` is the laptop carrier: the same barrage, but that one zombie is hitting the wall
      // with a laptop, and a dull thud among the hard ones is the only clue in the soundtrack
      // that the crowd is not all doing the same thing.
      schedule(soft ? 'thud_soft' : 'thud_wall', {
        pitch: pitched(IMPACT_PITCH_SPREAD, ((seed >>> 8) & 0xff) / 255),
      })
    },

    groan(square: string, sample: string | undefined, pitch: number): void {
      if (!limiter.allow('groan', deps.now(), square)) return
      // The skin names its own voice (`sounds.groan` in the skin JSON), but the skin is art data
      // and the sprite is code: an unknown name falls back rather than throwing inside a frame.
      const name: SpriteName = sample === 'groan_high' || sample === 'groan_low' ? sample : 'groan_low'
      schedule(name, { pitch })
    },

    gateCrash(square: string, pitch = 1): void {
      if (!limiter.allow('gate_crash', deps.now(), square)) return
      schedule('gate_crash', pitch === 1 ? {} : { pitch })
    },

    rebuild(): void {
      // Scheduled on the audio clock rather than three `setTimeout`s: the hammer blows are a
      // rhythm, and a timer that slips 30 ms under load turns a repair into a stumble.
      for (let i = 0; i < REBUILD_HITS; i++) {
        schedule('rebuild_hammer', { delay: i * REBUILD_SPACING_SECONDS })
      }
    },

    siegeStart(square: string): void {
      besieged.add(square)
      clearDayTimer()
      board.setAmbient('siege')
    },

    siegeEnd(square: string): void {
      if (!besieged.delete(square)) return
      clearDayTimer()
      // Delayed rather than immediate so the cheer lands over the siege bed and the birds come
      // back under it. Driven off the square's state, so a square that goes quiet without an
      // `allclear` frame — a resync after ten minutes away — still gets its daylight back.
      dayTimer = setTimeout(() => {
        dayTimer = null
        board.setAmbient(ambientForState())
      }, ALL_CLEAR_TO_DAY_MS)
    },

    cheer(): void {
      schedule('cheer')
    },

    codexReset(): void {
      if (!limiter.allow('reset_fanfare', deps.now())) return
      schedule('reset_fanfare')
    },

    dispose(): void {
      disposed = true
      clearDayTimer()
      stopBed()
      if (ctx) {
        document.removeEventListener('visibilitychange', onVisibility)
        void ctx.close()
      }
      ctx = null
      master = null
      kindGains = null
      buffer = null
      map = null
      limiter.reset()
    },
  }

  return board
}

/**
 * The page's one sound board.
 *
 * A module singleton because the scene, the button and the header all reach for the same audio
 * output and none of them owns it; passing a board down through the render tree would put an
 * audio dependency in the signature of every component between the header and a zombie.
 */
export const sound: SoundBoard = createSoundEngine()
